/**
 * UsersTableRow - Linha da tabela de usuários
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  Avatar,
  Chip,
  Tooltip,
  IconButton,
  Checkbox,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LinkIcon from '@mui/icons-material/Link';
import LoginIcon from '@mui/icons-material/Login';
import dayjs from 'dayjs';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import CollectionActions from './CollectionActions';

// Helper para formatar data de expiração
const formatExpirationDate = (expirationTime) => {
  if (!expirationTime) return '—';
  try {
    const dateOnly = expirationTime.slice(0, 10);
    return dayjs(dateOnly).format(window.__TRACCAR_DATE_FORMAT || 'DD/MM/YYYY');
  } catch {
    return expirationTime;
  }
};

// Helper para cores do status - USA TOKENS DINÂMICOS
const getStatusColors = (disabled, tokens) => {
  if (disabled) {
    return {
      text: tokens?.semantic?.error,
      bg: tokens?.semantic?.errorBg,
      border: `${tokens?.semantic?.error}40`,
    };
  }
  return {
    text: tokens?.semantic?.success,
    bg: tokens?.semantic?.successBg,
    border: `${tokens?.semantic?.success}40`,
  };
};

const UsersTableRow = memo(({
  row,
  onRefresh,
  onNavigate,
  isAdmin,
  currentUserId,
  onLogin,
  selected = false,
  onSelect,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const isDark = tokens?.isDark ?? true;

  const statusColors = getStatusColors(row.disabled, tokens);

  // Cor do avatar baseada se é admin ou não - USA TOKENS DINÂMICOS
  const avatarColor = row.administrator
    ? tokens?.semantic?.info
    : tokens?.semantic?.success;

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 0.5, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar */}
      <TableCell sx={{ py: 0.5, width: 48 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: `${avatarColor}20`,
            border: `1px solid ${avatarColor}40`,
          }}
        >
          {row.administrator ? (
            <AdminPanelSettingsIcon sx={{ fontSize: 18, color: avatarColor }} />
          ) : (
            <PersonIcon sx={{ fontSize: 18, color: avatarColor }} />
          )}
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 0.5, width: 70 }}>
        <Typography variant="caption" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Name */}
      <TableCell sx={{ py: 0.5 }}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 180 }}>
          {row.name}
        </Typography>
      </TableCell>

      {/* Email */}
      <TableCell sx={{ py: 0.5 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.secondary }} noWrap>
          {row.email}
        </Typography>
      </TableCell>

      {/* Admin */}
      <TableCell sx={{ py: 0.5 }} align="center">
        {row.administrator ? (
          <Chip
            label="Admin"
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              bgcolor: tokens?.semantic?.infoBg,
              color: tokens?.semantic?.info,
              border: `1px solid ${tokens?.semantic?.info}40`,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: tokens?.text?.tertiary }}>
            —
          </Typography>
        )}
      </TableCell>

      {/* Status */}
      <TableCell align="center" sx={{ py: 0.5 }}>
        <Chip
          label={row.disabled ? t('sharedDisabled') || 'Inativo' : t('sharedActive') || 'Ativo'}
          size="small"
          className={`status-${row.disabled ? 'disabled' : 'active'}`}
          sx={{
            minWidth: 65,
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: statusColors.bg,
            color: statusColors.text,
            border: `1px solid ${statusColors.border}`,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </TableCell>

      {/* Expiration */}
      <TableCell sx={{ py: 0.5 }}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: tokens?.text?.secondary }}>
          {formatExpirationDate(row.expirationTime)}
        </Typography>
      </TableCell>

      {/* Actions */}
      <TableCell align="right" sx={{ py: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
          <CollectionActions
            itemId={row.id}
            editPath="/settings/user"
            endpoint="users"
            setTimestamp={onRefresh}
            customActions={[
              {
                key: 'connections',
                title: t('sharedConnections'),
                icon: <LinkIcon />,
                variant: 'warning',
                handler: (id) => onNavigate(`/settings/user/${id}/connections`),
              },
            ]}
          />
          {/* Login as user - only for admin and not self */}
          {isAdmin && row.id !== currentUserId && (
            <Tooltip title={t('loginLogin') || 'Entrar como usuário'}>
              <IconButton
                size="small"
                onClick={() => onLogin(row.id)}
                sx={{
                  color: tokens?.semantic?.success,
                  '&:hover': {
                    backgroundColor: `${tokens?.semantic?.success}15`,
                  },
                }}
              >
                <LoginIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
});

UsersTableRow.displayName = 'UsersTableRow';

export default UsersTableRow;
